// AddHolidayModal.jsx
/* eslint-disable max-len */
import React, { useState, useEffect } from "react";
import moment from "moment";
import CustomeDatePicker from "../../components/DatePicker";
import Input from "../../components/InputBox";
import { dataService } from "../../services/data.services";
import Loader from "../../components/Loader";
import CommonModal from '../../components/Modal';
import { formatHolidayDate, isHolidayPast } from "./CommonMethods";

/**
 * Props:
 * - academicYear, classItem, section, branch_id, setToastmessage, open, onClose
 * - getHolidays (function) - optional; called after successful save to refresh calendar
 * - setToastErrorMessage (function) optional
 * - selectedDate (Date|string|moment)
 */
const AddHolidayModal = ({
    academicYear,
    classItem,
    section,
    holidayScope = "STUDENT",
    branch_id,
    open,
    onClose,
    getHolidays,
    setToastmessage,
    selectedDate,
    holidayData,
    initialEditModeScreen = "view",
    allowInlineDelete = true,
    deleteConfirmationMessage = "",
}) => {
    // UI / local state
    const [activeTab, setActiveTab] = useState("single"); // single | range | rules
    const [loading, setLoading] = useState(false);

    // Specific Days (rows) — we store date as 'YYYY-MM-DD' string only
    const [rows, setRows] = useState([{ date: "", name: "", reason: "" }]);

    // Range tab (can keep raw picker values; we normalize on save)
    const [rangeStart, setRangeStart] = useState("");
    const [rangeEnd, setRangeEnd] = useState("");
    const [rangeCommonName, setRangeCommonName] = useState("");
    const [rangeCommonReason, setRangeCommonReason] = useState("");

    // Rules tab (weekend rules)
    const [ruleIncludeSaturdays, setRuleIncludeSaturdays] = useState(true);
    const [ruleIncludeSundays, setRuleIncludeSundays] = useState(true);
    const [ruleSaturdayHalf, setRuleSaturdayHalf] = useState("FULL"); // "FULL" | "FIRST_HALF" | "SECOND_HALF"
    const [rulesPreviewDates, setRulesPreviewDates] = useState([]);
    const [rulesMonth, setRulesMonth] = useState(() => moment());

    // Misc validation hooks used by your custom inputs
    const [submitSelected, setSubmitSelected] = useState(false);
    const [dateError, setDateError] = useState("");
    const [isEditMode, setIsEditMode] = useState(false);
    const [editModeScreen, setEditModeScreen] = useState("view"); // view | edit | delete
    const [editForm, setEditForm] = useState({ name: "", reason: "" });
    const [modalError, setModalError] = useState("");

    // ---------- Date helpers ----------
    const toYMD = (val) => {
        if (!val) return "";
        // Day.js object
        if (val?.$d instanceof Date) return moment(val.$d).format("YYYY-MM-DD");
        // Date
        if (val instanceof Date) return moment(val).format("YYYY-MM-DD");
        // moment
        if (moment.isMoment(val)) return val.format("YYYY-MM-DD");
        // string
        if (typeof val === "string") {
            const m = moment(val, ["YYYY-MM-DD", "MMM DD, YYYY"], true);
            return m.isValid() ? m.format("YYYY-MM-DD") : "";
        }
        return "";
    };

    const enumerateRange = (startVal, endVal) => {
        try {
            const start = toYMD(startVal);
            const end = toYMD(endVal);
            if (!start || !end) return [];
            if (moment(start).isAfter(end)) return [];

            const arr = [];
            let cur = moment(start);
            const stop = moment(end);
            while (!cur.isAfter(stop)) {
                arr.push(cur.format("YYYY-MM-DD"));
                cur.add(1, "day");
            }
            return arr;
        } catch (err) {
            console.error("Error in enumerateRange:", err);
            return [];
        }
    };

    const genWeekendRuleDates = (
        monthMoment,
        includeSat,
        includeSun,
        saturdayHalf
    ) => {
        const base =
      monthMoment && moment(monthMoment).isValid()
          ? moment(monthMoment)
          : moment();
        const start = base.clone().startOf("month");
        const end = base.clone().endOf("month");
        const arr = [];
        let cur = start.clone();
        while (!cur.isAfter(end)) {
            const dow = cur.day(); // 0 Sun ... 6 Sat
            if (includeSat && dow === 6)
                arr.push({ date: cur.format("YYYY-MM-DD"), half: saturdayHalf });
            if (includeSun && dow === 0)
                arr.push({ date: cur.format("YYYY-MM-DD"), half: "FULL" });
            cur.add(1, "day");
        }
        const uniq = new Map();
        arr.forEach((p) => uniq.set(p.date, p));
        return Array.from(uniq.values()).sort((a, b) =>
            a.date.localeCompare(b.date)
        );
    };

    const getAcademicYearDateMoment = (dateValue) => {
        if (!dateValue) return null;
        const normalizedDate = String(dateValue).split("T")[0];
        const parsedDate = moment(normalizedDate, "YYYY-MM-DD", true);
        return parsedDate.isValid() ? parsedDate : null;
    };

    const getRulesBaseMonth = () => {
        const academicYearStart = getAcademicYearDateMoment(academicYear?.start_date);

        if (academicYearStart?.isValid()) {
            return academicYearStart.startOf("month");
        }

        return moment().startOf("month");
    };

    const getAcademicYearMonthOptions = () => {
        const academicYearStart = getAcademicYearDateMoment(academicYear?.start_date);
        const academicYearEnd = getAcademicYearDateMoment(academicYear?.end_date);

        if (!academicYearStart?.isValid() || !academicYearEnd?.isValid()) {
            return [];
        }

        const options = [];
        const current = academicYearStart.clone().startOf("month");
        const end = academicYearEnd.clone().startOf("month");

        while (!current.isAfter(end, "month")) {
            options.push({
                name: current.format("MMM YYYY"),
                value: current.format("YYYY-MM"),
            });
            current.add(1, "month");
        }

        return options;
    };

    const rulesMonthOptions = getAcademicYearMonthOptions();

    // recompute preview for rules when toggles change
    useEffect(() => {
        setRulesPreviewDates(
            genWeekendRuleDates(
                rulesMonth,
                ruleIncludeSaturdays,
                ruleIncludeSundays,
                ruleSaturdayHalf
            )
        );
    }, [rulesMonth, ruleIncludeSaturdays, ruleIncludeSundays, ruleSaturdayHalf]);

    // rows helpers
    const addEmptyRow = () =>
        setRows((prev) => [...prev, { date: "", name: "", reason: "" }]);
    const removeRow = (idx) =>
        setRows((prev) => {
            if (prev.length === 1) return [{ date: "", name: "", reason: "" }];
            return prev.filter((_, i) => i !== idx);
        });
    const updateRow = (idx, patch) =>
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const isSpecificRowEmpty = (row) => !toYMD(row?.date) && !String(row?.name || "").trim();
    const isSpecificRowPartial = (row) => {
        const hasDate = !!toYMD(row?.date);
        const hasName = !!String(row?.name || "").trim();
        return hasDate !== hasName;
    };

    const selectedDateFormatted = toYMD(selectedDate);
    const selectedDateLabel = selectedDateFormatted
        ? formatHolidayDate(selectedDateFormatted)
        : "";
    const rangeStartDate = toYMD(holidayData?.start_date);
    const rangeEndDate = toYMD(holidayData?.end_date);
    const isRangeHoliday = !!holidayData?.holiday_range_id
        || (!!rangeStartDate && !!rangeEndDate && rangeStartDate !== rangeEndDate);
    const rangeTitleLabel = isRangeHoliday
        ? `${formatHolidayDate(rangeStartDate)} - ${formatHolidayDate(rangeEndDate)}`
        : "";
    const isPastHolidayRecord = isEditMode && isHolidayPast(holidayData);
    const getStudentScopeLabel = () => {
        if (!academicYear?.name) return "";
        if (classItem?.name && section?.name) {
            return `Adding Holiday(s) for: ${academicYear.name} > ${classItem.name} > ${section.name}`;
        }
        if (classItem?.name) {
            return `Adding Holiday(s) for: ${academicYear.name} > ${classItem.name}`;
        }
        return `Adding Holiday(s) for: ${academicYear.name}`;
    };

    const getSelectedScopePayload = () => {
        const classId = classItem?._id || null;
        const sectionId = section?.id || null;

        if (sectionId && !classId) {
            return {
                error: "Section can be selected only when exactly one class is selected.",
                classId,
                sectionId,
            };
        }

        return {
            error: null,
            classId,
            sectionId,
        };
    };

    const getHolidayIdentifierPayload = () => {
        const scope = String(holidayScope || "").toUpperCase();
        return {
            branch_id,
            holiday_range_id: holidayData?.holiday_range_id || null,
            
            branch_holiday_id: !holidayData?.holiday_range_id && scope === "BRANCH"
                ? holidayData?.branch_holiday_id || null
                : null,
            holiday_main_id: !holidayData?.holiday_range_id && scope === "STUDENT"
                ? holidayData?.holiday_main_id || null
                : null,
            employee_holiday_id: !holidayData?.holiday_range_id && scope === "EMPLOYEE"
                ? holidayData?.employee_holiday_id || null
                : null,
        };
    };

    const finishHolidayAction = async (message) => {
        onClose?.();
        if (typeof getHolidays === "function") {
            try {
                await getHolidays();
            } catch (err) {
                console.warn("getHolidays failed after holiday action:", err);
            }
        }
        setTimeout(() => setToastmessage?.(message), 100);
    };

    const showModalError = (message) => {
        setModalError(message || "");
    };

    // --- Apply actions ---
    const applySpecificDays = async () => {
        setSubmitSelected(true);
        const normalizedRows = rows.map((r) => ({
            ...r,
            date: toYMD(r.date),
            name: String(r.name || "").trim(),
            reason: String(r.reason || "").trim(),
        }));

        const hasPartialRow = normalizedRows.some(isSpecificRowPartial);
        if (hasPartialRow) {
            showModalError("Please provide date and name for each filled row.");
            return;
        }

        const valid = normalizedRows.filter((r) => !isSpecificRowEmpty(r));

        if (!valid.length) {
            showModalError("Please provide date and name for at least one row.");
            return;
        }

        const holidaysToSave = valid.map((r) => ({
            name: r.name.trim(),
            reason: (r.reason || "").trim(),
            holiday_date: r.date, // already YYYY-MM-DD
        }));
        await saveHolidaysRemote({
            holiday_type: "SPECIFIC",
            payload: { holidays: holidaysToSave },
        });
    };

    const applyRange = async () => {
        const dates = enumerateRange(rangeStart, rangeEnd);
        if (!rangeStart || !rangeEnd || !rangeCommonName?.trim()) {
            showModalError("Please provide start date, end date and a common name.");
            return;
        }
        if (!dates.length) {
            showModalError("Invalid range.");
            return;
        }
        await saveHolidaysRemote({
            holiday_type: "DATE_RANGE",
            payload: {
                start_date: toYMD(rangeStart),
                end_date: toYMD(rangeEnd),
                name: rangeCommonName.trim(),
                reason: (rangeCommonReason || "").trim(),
            },
        });
    };

    const applyRules = async () => {
        const prev = genWeekendRuleDates(
            rulesMonth,
            ruleIncludeSaturdays,
            ruleIncludeSundays,
            ruleSaturdayHalf
        );
        if (!prev.length) {
            showModalError("No dates to apply for selected rules.");
            return;
        }
        const rulePayload = [{
            month: moment(rulesMonth).format("YYYY-MM"),
            saturday: {
                enabled: !!ruleIncludeSaturdays,
                type: ruleIncludeSaturdays ? ruleSaturdayHalf : "FULL",
            },
            sunday: {
                enabled: !!ruleIncludeSundays,
                type: "FULL",
            },
        }];
        await saveHolidaysRemote({
            holiday_type: "WEEKEND_RULE",
            payload: rulePayload,
        });
    };

    const handleUpdateHoliday = async () => {
        const name = editForm?.name?.trim();
        if (!branch_id) {
            showModalError("Branch required");
            return;
        }
        if (!name) {
            setSubmitSelected(true);
            showModalError("Holiday name is required");
            return;
        }
        const { error } = getSelectedScopePayload();
        if (error) {
            showModalError(error);
            return;
        }

        try {
            setLoading(true);
            const body = {
                ...getHolidayIdentifierPayload(),
                academic_year_id: academicYear?._id,
                name,
                reason: (editForm?.reason || "").trim(),
            };
            console.log('body for update', body)
            const response = await dataService.PutAPI(body, "holidays/update");
            if (!response.error) {
                await finishHolidayAction("Holiday updated successfully!");
            } else {
                const msg = response?.data?.message || "Failed to update holiday";
                showModalError(msg);
            }
        } catch (err) {
            console.error("Error updating holiday:", err);
            const msg = err?.response?.data?.message || "Error updating holiday";
            showModalError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteHoliday = async () => {
        if (!branch_id) {
            showModalError("Branch required");
            return;
        }
        if (isPastHolidayRecord) {
            showModalError("Past holiday cannot be deleted");
            return;
        }
        const { error } = getSelectedScopePayload();
        if (error) {
            showModalError(error);
            return;
        }

        try {
            setLoading(true);
            const body = {
                ...getHolidayIdentifierPayload(),
                academic_year_id: academicYear?._id,
            };
            console.log('academicYear?._id',academicYear?._id)
            const response = await dataService.PutAPI(body, "holidays/delete");
            if (!response.error) {
                await finishHolidayAction("Holiday deleted successfully");
            } else {
                const msg = response?.data?.message || "Failed to delete holiday";
                showModalError(msg);
            }
        } catch (err) {
            console.error("Error deleting holiday:", err);
            const msg = err?.response?.data?.message || "Error deleting holiday";
            showModalError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Core: Post payload to API
    const saveHolidaysRemote = async ({ holiday_type, payload }) => {
        if (!holiday_type || !payload) return;
        if (!branch_id || !academicYear?._id) {
            showModalError("Branch and Academic Year required");
            return;
        }
        
        const classId = classItem?._id || null;
        const sectionId = section?.id || null;

        if (sectionId && !classId) {
            showModalError("Section can be selected only when exactly one class is selected.");
            return;
        }
        

        const isBranchHoliday = holidayScope === "BRANCH";
        const isEmployeeHoliday = holidayScope === "EMPLOYEE";
        const isStudentHoliday = !isBranchHoliday && !isEmployeeHoliday;

        try {
            setLoading(true);
            const body = {
                branch_id,
                academic_year_id: academicYear?._id,
                holiday_type,
                is_student_holiday: isBranchHoliday || isStudentHoliday,
                is_employee_holiday: isBranchHoliday || isEmployeeHoliday,
                is_branch_holiday: isBranchHoliday,
                class_id: classId,
                section_id: sectionId,
                payload,
            };

            const response = await dataService.PostAPI(body, `holidays/create`); 
            const responseStatus = response?.data?.status;
            if (!response?.error && responseStatus >= 200 && responseStatus < 300) {
                await finishHolidayAction("Holiday(s) added successfully!");
            } else {
                const msg = response?.data?.data?.message || "Failed to add holiday(s)";
                showModalError(msg);
            }
        } catch (err) {
            console.error("Error adding holiday(s):", err);
            const msg = err?.response?.data?.message || "Error adding holiday(s)";
            showModalError(msg);
        } finally {
            setLoading(false);
        }
    };

    // reset local state when modal closes
    useEffect(() => {
        if (!open) {
            setActiveTab("single");
            setRows([{ date: "", name: "", reason: "" }]);
            setRangeStart("");
            setRangeEnd("");
            setRangeCommonName("");
            setRangeCommonReason("");
            setRuleIncludeSaturdays(true);
            setRuleIncludeSundays(true);
            setRuleSaturdayHalf("FULL");
            setRulesMonth(getRulesBaseMonth());
            setSubmitSelected(false);
            setDateError("");
            setEditModeScreen("view");
            setEditForm({ name: "", reason: "" });
            setModalError("");
        }
    }, [open]);

    useEffect(() => {
        if (!open || isEditMode) return;
        setRulesMonth(getRulesBaseMonth());
    }, [open, isEditMode, academicYear?.start_date]);

    useEffect(() => {
        setModalError("");
    }, [activeTab, editModeScreen]);

    useEffect(() => {
        if (!modalError) return undefined;
        const timer = setTimeout(() => {
            setModalError("");
        }, 3000);
        return () => clearTimeout(timer);
    }, [modalError]);

    // prefill first row from parent-selected date
    useEffect(() => {
        if (open && selectedDate) {
            const formatted = toYMD(selectedDate);
            if (!formatted) return;

            // if holidayData exists → preload name & reason
            if (holidayData) {
                setRows([
                    {
                        date: formatted,
                        name: holidayData.name || "",
                        reason: holidayData.reason || "",
                    },
                ]);
                setIsEditMode(true);
                setEditModeScreen(initialEditModeScreen);
                setEditForm({
                    name: holidayData.name || "",
                    reason: holidayData.reason || "",
                });
            } else {
                // otherwise → only preload date, empty name & reason
                setRows([
                    {
                        date: formatted,
                        name: "",
                        reason: "",
                    },
                ]);
                
                setIsEditMode(false);
                setEditModeScreen("view");
                setEditForm({ name: "", reason: "" });
            }
        } else {
            setIsEditMode(false);
            setEditModeScreen("view");
            setEditForm({ name: "", reason: "" });
        }
    }, [open, selectedDate, holidayData, initialEditModeScreen]);


    if (!open) return null;

    return (
        <CommonModal
            show={true}
            handleClose={() => { onClose(); }}
            title={isEditMode
                ? isRangeHoliday
                    ? `Update Holiday Range: ${rangeTitleLabel}`
                    : `Update Holiday: ${selectedDateLabel}`
                : selectedDate ? "Add Holiday" : "Add Holiday(s)"}
            className="font-normal p-4"
            dialogueWidth={isEditMode?'600px':'750px' }
            dialogueHeight={isEditMode?'fit-content':'495px'}
            dialogTopOffset={25}>
            {/* Tabs */}
            <div className='px-4 relative'>
                {!isEditMode && (
                    <div className="mb-2 rounded border border-[#D0D5DD] bg-[#F8FAFC] px-3 py-2 text-sm text-[#344054]">
                        {getStudentScopeLabel()}
                    </div>
                )}
                {selectedDate || isEditMode ? (
                    <></>
                ) : (
                    <div className="flex gap-2 mb-2">
                        <button
                            onClick={() => setActiveTab("single")}
                            className={`px-3 py-2 rounded ${activeTab === "single"
                                ? "bg-[#eef2ff]  border"
                                : "bg-white border"
                            }`}
                        >
                            Specific Days
                        </button>
                        <button
                            onClick={() => setActiveTab("range")}
                            className={`px-3 py-2 rounded ${activeTab === "range"
                                ? "bg-[#eef2ff] border"
                                : "bg-white border"
                            }`}
                        >
                            Date Range
                        </button>
                        <button
                            onClick={() => setActiveTab("rules")}
                            className={`px-3 py-2 rounded ${activeTab === "rules"
                                ? "bg-[#eef2ff]  border"
                                : "bg-white border"
                            }`}
                        >
                            Rules (Weekends)
                        </button>
                    </div>

                )}
                {/* Tab contents */}
                {modalError && (
                    <div className="absolute left-4 right-4 top-10 z-20 rounded border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B42318] shadow-sm">
                        {modalError}
                    </div>
                )}
                <div
                    style={activeTab !== "rules" && !isEditMode ? { height: "275px", overflowY: "auto" } : {}}
                >
                    {isEditMode && editModeScreen === "view" && (
                        <div className="">
                            <div className="right gap-4" style={{'float': 'right'}}>
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-white"
                                    onClick={() => {
                                        setEditForm({
                                            name: rows[0]?.name || "",
                                            reason: rows[0]?.reason || "",
                                        });
                                        setEditModeScreen("edit");
                                    }}
                                >
                                    <img
                                        src="/images/student-edit-icon.svg"
                                        alt="Update Holiday"
                                        className="w-5 h-5"
                                    />
                                </button>
                                {allowInlineDelete && (
                                    <button
                                        type="button"
                                        className="py-2 bg-white text-[#B42318]"
                                        onClick={() => setEditModeScreen("delete")}
                                    >
                                        <img
                                            src="/images/delete-bg-red-icon.svg"
                                            alt="Delete Holiday"
                                            className="w-5 h-5"
                                        />
                                    </button>
                                )}
                            </div>
                            <div className="px-4">
                                <div className="grid gap-4">
                                    <div className="flex items-start gap-3">
                                        <label className="min-w-[130px] text-sm font-semibold text-[#031624]">
                                            Holiday Name
                                        </label>
                                        <span className="text-sm text-[#344054] break-words">
                                            {rows[0]?.name || "-"}
                                        </span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <label className="min-w-[130px] text-sm font-semibold text-[#031624]">
                                            Reason
                                        </label>
                                        <span className="text-sm text-[#344054] break-words">
                                            {rows[0]?.reason || "-"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            
                        </div>
                    )}

                    {isEditMode && editModeScreen === "edit" && (
                        <div className="overflow-auto" style={{ maxHeight: "300px",overflowY: "auto" }}>
                            <table className="w-full" id="admin-table">
                                <thead className="bg-[#f3f4f6] text-sm font-semibold">
                                    <tr>
                                        <th className="text-left w-[35%]">Holiday Name</th>
                                        <th className="text-left w-[45%]">Reason (Optional)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-t align-top">
                                        <td className="px-2 py-2 min-w-[180px]">
                                            <Input
                                                type="text"
                                                id="name"
                                                placeholder="Holiday Name"
                                                state={editForm}
                                                setsubmitSelected={setSubmitSelected}
                                                handleInput={(e) =>
                                                    setEditForm((prev) => ({
                                                        ...prev,
                                                        name: e.target.value,
                                                    }))
                                                }
                                            />
                                        </td>
                                        <td className="px-2 py-2 min-w-[180px]">
                                            <Input
                                                type="text"
                                                id="reason"
                                                placeholder="Enter Reason"
                                                state={editForm}
                                                setsubmitSelected={setSubmitSelected}
                                                handleInput={(e) =>
                                                    setEditForm((prev) => ({
                                                        ...prev,
                                                        reason: e.target.value,
                                                    }))
                                                }
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {isEditMode && editModeScreen === "delete" && (
                        <div className="border rounded p-5 bg-[#FFF7F7] text-center">
                            <p className="text-sm text-[#101828]">
                                {isPastHolidayRecord
                                    ? "Past Holiday. Cannot be deleted"
                                    : (deleteConfirmationMessage || "Are you sure you want to delete this holiday?")}
                            </p>
                        </div>
                    )}

                    {!isEditMode && activeTab === "single" && (
                        <div className="overflow-auto">
                            <table className="w-full" id="admin-table">
                                <thead className="bg-[#f3f4f6] text-sm font-semibold">
                                    <tr>
                                        {!isEditMode && <th className="text-left w-[25%]">Date</th>}
                                        <th className="text-left w-[25%]">Name</th>
                                        <th className="text-left w-[25%]">Reason (Optional)</th>
                                        <th className="text-center w-[15%]">&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, idx) => (
                                        <tr key={idx} className="border-t align-top">
                                            {!isEditMode && (
                                                <td className="px-2 py-2 min-w-[180px]">
                                                    <CustomeDatePicker
                                                        id="date"
                                                        format="MMM DD, YYYY"
                                                        state={r}
                                                        placeholder="Select Date"
                                                        showErrorBorder={submitSelected && isSpecificRowPartial(r) && !toYMD(r.date)}
                                                        setdateError={setDateError}
                                                        isMandatoryField={true}
                                                        imageClass={true}
                                                        handleSelect={(id, dateVal) =>
                                                            updateRow(idx, { date: toYMD(dateVal) })
                                                        }
                                                        setsubmitSelected={setSubmitSelected}
                                                    />
                                                </td>
                                            )}
                                            <td className="px-2 py-2 min-w-[180px]">
                                                <Input
                                                    type="text"
                                                    id="name"
                                                    placeholder="Holiday Name"
                                                    state={r || ""}
                                                    optional={isSpecificRowEmpty(r) ? true : undefined}
                                                    showErrorBorder={submitSelected && isSpecificRowPartial(r) && !String(r.name || "").trim()}
                                                    setsubmitSelected={setSubmitSelected}
                                                    handleInput={(e) =>
                                                        updateRow(idx, { name: e.target.value })
                                                    }
                                                />
                                            </td>
                                            <td className="px-2 py-2 min-w-[180px]">
                                                <Input
                                                    type="text"
                                                    id="reason"
                                                    placeholder="Enter Reason"
                                                    state={r || ""}
                                                    setsubmitSelected={setSubmitSelected}
                                                    handleInput={(e) =>
                                                        updateRow(idx, { reason: e.target.value })
                                                    }
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <div className="flex justify-center">
                                                    <button
                                                        className="px-2 py-1"
                                                        onClick={() => removeRow(idx)}
                                                    >
                                                        <img
                                                            src="/images/delete-bg-red-icon.svg"
                                                            alt="Delete"
                                                        />
                                                    </button>
                                                    { <button
                                                        className="px-2 py-1"
                                                        onClick={addEmptyRow}
                                                    >
                                                        <img src="/images/add-icon-blue.svg" alt="Add More" id="plus_iconn" className="cursor-pointer add-icon" />
                                                    </button>}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === "range" && (
                        <div className="overflow-auto">
                            <table className="w-full" id="admin-table">
                                <thead className="bg-[#f3f4f6] text-sm font-semibold">
                                    <tr>
                                        <th className="text-left">Start Date</th>
                                        <th className="text-left">End Date</th>
                                        <th className="text-left">Common Name</th>
                                        <th className="text-left">Common Reason<span className='text-xs'>(Optional)</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-t align-top">
                                        <td className="px-2 py-2 min-w-[180px]">
                                            <CustomeDatePicker
                                                id="rangeStart"
                                                format="MMM DD, YYYY"
                                                state={rangeStart}
                                                placeholder="Start Date"
                                                setdateError={setDateError}
                                                imageClass={true}
                                                handleSelect={(id, d) => setRangeStart(d)}
                                                setsubmitSelected={setSubmitSelected}
                                            />
                                        </td>
                                        <td className="px-2 py-2 min-w-[180px]">
                                            <CustomeDatePicker
                                                id="rangeEnd"
                                                format="MMM DD, YYYY"
                                                state={rangeEnd}
                                                setdateError={setDateError}
                                                imageClass={true}
                                                placeholder="End Date"
                                                handleSelect={(id, d) => setRangeEnd(d)}
                                                setsubmitSelected={setSubmitSelected}
                                            />
                                        </td>
                                        <td className="px-2 py-2 min-w-[180px]">
                                            <Input
                                                placeholder="Common Name"
                                                state={rangeCommonName || ""}
                                                handleInput={(e) => setRangeCommonName(e.target.value)}
                                                setsubmitSelected={setSubmitSelected}
                                            />
                                        </td>
                                        <td className="px-2 py-2 min-w-[180px]">
                                            <Input
                                                placeholder="Common Reason"
                                                state={rangeCommonReason || ""}
                                                handleInput={(e) => setRangeCommonReason(e.target.value)}
                                                setsubmitSelected={setSubmitSelected}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === "rules" && (
                        <div className="">
                            <div className="overflow-auto">
                                <table className="w-full" id="admin-table">
                                    <thead className="bg-[#f3f4f6] text-sm font-semibold">
                                        <tr>
                                            <th className="text-left w-[20%]">Month</th>
                                            <th className="text-left w-[40%]">Saturday Rule</th>
                                            <th className="text-left w-[40%]">Sunday Rule</th>
                                            
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-t align-top">
                                            <td className="px-2 py-2 min-w-[100px]">
                                                <select
                                                    name="rules_month"
                                                    id="rules_month"
                                                    data-testid="rules_month"
                                                    className="border rounded px-2 py-1 text-sm"
                                                    value={moment(rulesMonth).format("YYYY-MM")}
                                                    onChange={(e) => {
                                                        setRulesMonth(moment(e.target.value, "YYYY-MM"));
                                                    }}
                                                >
                                                    {rulesMonthOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2 min-w-[210px]">
                                                <label className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={ruleIncludeSaturdays}
                                                        onChange={(e) =>
                                                            setRuleIncludeSaturdays(e.target.checked)
                                                        }
                                                    />
                                                    <div className="flex items-center gap-2 ">
                                                        <span className="">All Saturdays</span>
                                                        <select
                                                            className="border rounded px-2 py-1 text-sm"
                                                            value={ruleSaturdayHalf}
                                                            onChange={(e) => setRuleSaturdayHalf(e.target.value)}
                                                            disabled={!ruleIncludeSaturdays}
                                                        >
                                                            <option value="FULL">Full day</option>
                                                            <option value="FIRST_HALF">First half</option>
                                                            <option value="SECOND_HALF">Second half</option>
                                                        </select>
                                                    </div>
                                                </label>
                                            </td>
                                            <td className="px-2 py-2 min-w-[210px]">
                                                <label className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={ruleIncludeSundays}
                                                        onChange={(e) =>
                                                            setRuleIncludeSundays(e.target.checked)
                                                        }
                                                    />
                                                    All Sundays (Full day)
                                                </label>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-sm text-[#344054] mt-3 mb-1">{rulesPreviewDates.length} holidays will be created.</p>
                            <div className="scroll-bar overflow-auto border rounded p-2 text-xs" style={{'height': '9.5rem'}}>
                                {rulesPreviewDates.map((p) => (
                                    <div key={p.date}>
                                        {formatHolidayDate(p.date)}{" "}
                                        {p.half && p.half !== "FULL"
                                            ? `(${p.half === "FIRST_HALF"
                                                ? "First Half"
                                                : "Second Half"
                                            })`
                                            : ""}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex w-fit mx-auto p-3 pt-1 mt-[10px]">
                    {!(isEditMode && editModeScreen === "delete" && isPastHolidayRecord) && (
                        <button
                            onClick={isEditMode && editModeScreen !== "view"
                                ? () => setEditModeScreen("view")
                                : onClose}
                            className="cancl-btn1"
                        >
                            Cancel
                        </button>
                    )}

                    {isEditMode && editModeScreen === "edit" && (
                        <button
                            type="button"
                            onClick={handleUpdateHoliday}
                            disabled={loading}
                            className={`actn-btn1 ${loading ? "opacity-70 cursor-not-allowed" : ""
                            }`}
                        >
                            {loading ? <Loader /> : "Save"}
                        </button>
                    )}

                    {isEditMode && editModeScreen === "delete" && (
                        isPastHolidayRecord ? (
                            <button
                                type="button"
                                onClick={onClose}
                                className="actn-btn1"
                            >
                                OK
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleDeleteHoliday}
                                disabled={loading}
                                className={`actn-btn1 ${loading ? "opacity-70 cursor-not-allowed" : ""
                                }`}
                            >
                                {loading ? <Loader /> : "Delete"}
                            </button>
                        )
                    )}

                    {!isEditMode && activeTab === "single" && (
                        <button
                            type="button"
                            onClick={applySpecificDays}
                            disabled={loading}
                            className={`actn-btn1 ${loading ? "opacity-70 cursor-not-allowed" : ""
                            }`}
                        >
                            {loading ? <Loader /> : "Save"}
                        </button>
                    )}

                    {!isEditMode && activeTab === "range" && (
                        <button
                            type="button"
                            onClick={applyRange}
                            disabled={loading}
                            className={`actn-btn1 onboardInstitution ${loading ? "opacity-70 cursor-not-allowed" : ""
                            }`}
                        >
                            {loading ? <Loader /> : "Save Range"}
                        </button>
                    )}

                    {!isEditMode && activeTab === "rules" && (
                        <button
                            type="button"
                            onClick={applyRules}
                            disabled={loading}
                            className={`actn-btn1 onboardInstitution ${loading ? "opacity-70 cursor-not-allowed" : ""
                            }`}
                        >
                            {loading ? <Loader /> : "Apply Rules"}
                        </button>
                    )}
                </div>
            </div>
        </CommonModal>
    );
};

export default AddHolidayModal;
